import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { eur } from "@/data/ambassadorProgramme";

const err = (e) => toast.error(formatApiError(e.response?.data?.detail));
const STATUSES = ["", "pending", "on_hold", "eligible", "approved", "paid", "blocked", "rejected", "void"];

const Table = ({ cols, children, testid, min = 760 }) => (
  <div className="card-warm overflow-x-auto" data-testid={testid}>
    <table className="w-full text-sm" style={{ minWidth: min }}>
      <thead className="bg-[var(--muted-surface)] text-left"><tr>{cols.map((c) => <th key={c} className="p-2">{c}</th>)}</tr></thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

export const CommissionsPanel = () => {
  const [status, setStatus] = useState("eligible");
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get("/admin/ambassador-commissions", { params: status ? { status } : {} }).then((r) => setRows(r.data)).catch(err), [status]);
  useEffect(() => { load(); }, [load]);
  const act = async (c, action) => {
    const note = action === "approve" ? "" : window.prompt(`Note for ${action} (optional)`) ?? null;
    if (note === null) return;
    try { await api.post(`/admin/ambassador-commissions/${c.id}/action`, { action, note }); toast.success("Updated"); load(); } catch (e) { err(e); }
  };
  const bulk = async () => {
    try { const { data } = await api.post("/admin/ambassador-commissions/bulk-approve", { ids: rows.filter((r) => r.status === "eligible").map((r) => r.id) }); toast.success(`${data.approved} approved`); load(); } catch (e) { err(e); }
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        <select className="field text-sm" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="commission-status-filter">{STATUSES.map((s) => <option key={s} value={s}>{s || "All statuses"}</option>)}</select>
        {status === "eligible" && rows.length > 0 && <button className="chip text-xs bg-emerald-50" onClick={bulk} data-testid="commission-bulk-approve">Approve all eligible ({rows.length})</button>}
      </div>
      <Table cols={["Date", "Ambassador", "Learner", "Prog.", "Paid", "Commission", "Status", "Flags", "Actions"]} testid="admin-commissions-table" min={980}>
        {rows.length === 0 && <tr><td colSpan={9} className="p-4 text-center text-zinc-500">Nothing here.</td></tr>}
        {rows.map((c) => (
          <tr key={c.id} className="border-t border-[var(--border-warm)] align-top" data-testid={`commission-row-${c.id}`}>
            <td className="p-2">{c.created_at.slice(0, 10)}<div className="text-xs text-zinc-500">eligible {c.eligible_at.slice(0, 10)}</div></td>
            <td className="p-2">{c.ambassador_code}</td><td className="p-2 text-xs">{c.user_name}<br/>{c.user_email}</td><td className="p-2">{c.programme}</td>
            <td className="p-2">{eur(c.amount_paid)}</td><td className="p-2 font-semibold">{eur(c.commission_amount)} <span className="text-xs text-zinc-500">{c.rate_tier}</span></td>
            <td className="p-2 capitalize" data-testid={`commission-status-${c.id}`}>{c.status.replace("_", " ")}</td>
            <td className="p-2 text-xs text-red-700">{[...(c.block_reasons || []), ...(c.fraud_flags || [])].join(", ")}{c.void_reason && <div className="text-zinc-500">{c.void_reason}</div>}</td>
            <td className="p-2"><div className="flex flex-wrap gap-1">
              {["eligible", "on_hold"].includes(c.status) && <button className="chip text-xs bg-emerald-50" onClick={() => act(c, "approve")} data-testid={`commission-approve-${c.id}`}>Approve</button>}
              {c.status === "on_hold" && <button className="chip text-xs" onClick={() => act(c, "release")} data-testid={`commission-release-${c.id}`}>Release</button>}
              {!["paid", "void", "rejected", "blocked"].includes(c.status) && <>
                <button className="chip text-xs" onClick={() => act(c, "reject")} data-testid={`commission-reject-${c.id}`}>Reject</button>
                <button className="chip text-xs" onClick={() => act(c, "refund")} data-testid={`commission-refund-${c.id}`}>Refunded</button>
                <button className="chip text-xs" onClick={() => act(c, "chargeback")} data-testid={`commission-chargeback-${c.id}`}>Chargeback</button>
              </>}
            </div></td>
          </tr>
        ))}
      </Table>
    </div>
  );
};

export const PayoutsPanel = () => {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/ambassador-payouts").then((r) => setRows(r.data)).catch(err);
  useEffect(() => { load(); }, []);
  const generate = async () => {
    try { const { data } = await api.post("/admin/ambassador-payouts/generate"); toast.success(`${data.created.length} payouts scheduled, ${data.carried_forward.length} carried forward (< €50)`); load(); } catch (e) { err(e); }
  };
  const paid = async (p) => {
    const ref = window.prompt(`Wise transfer reference for ${p.ambassador_code} (${eur(p.total)})`);
    if (!ref) return;
    try { await api.post(`/admin/ambassador-payouts/${p.id}/mark-paid`, { wise_reference: ref }); toast.success("Marked as paid"); load(); } catch (e) { err(e); }
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        <button className="chip text-xs" onClick={generate} data-testid="payout-generate">Schedule payouts now</button>
        <span className="text-xs text-zinc-500">Approved commissions only · minimum €50 · runs automatically every Friday at 18:00 Spain time</span>
      </div>
      <Table cols={["Week", "Ambassador", "Wise / bank details", "Enrolments", "Amount", "Status", ""]} testid="admin-payouts-table" min={900}>
        {rows.length === 0 && <tr><td colSpan={7} className="p-4 text-center text-zinc-500">No payouts yet.</td></tr>}
        {rows.map((p) => {
          const d = p.payout_details || {};
          return (
            <tr key={p.id} className="border-t border-[var(--border-warm)] align-top" data-testid={`payout-row-${p.id}`}>
              <td className="p-2">{p.period}<div className="text-xs text-zinc-500">Fri {p.payout_date}</div></td>
              <td className="p-2">{p.ambassador_name}<div className="text-xs text-zinc-500">{p.ambassador_code}</div></td>
              <td className="p-2 text-xs">{d.account_holder ? <>{d.account_holder}<br/>{d.iban || d.account_number || d.wise_email} {d.swift_bic}<br/>{d.currency} · {d.country}</> : <span className="text-red-700">Missing details</span>}</td>
              <td className="p-2">{p.count}</td><td className="p-2 font-semibold">{eur(p.total)}</td>
              <td className="p-2 capitalize" data-testid={`payout-status-${p.id}`}>{p.status}{p.wise_reference && <div className="text-xs text-zinc-500">{p.wise_reference} · {p.paid_at?.slice(0, 10)}</div>}</td>
              <td className="p-2">{p.status === "scheduled" && <button className="chip text-xs bg-emerald-50" onClick={() => paid(p)} data-testid={`payout-mark-paid-${p.id}`}>Mark paid</button>}</td>
            </tr>
          );
        })}
      </Table>
    </div>
  );
};

export const FraudPanel = () => {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/ambassador-fraud-alerts").then((r) => setRows(r.data)).catch(err);
  useEffect(() => { load(); }, []);
  const dismiss = async (a) => { await api.post(`/admin/ambassador-fraud-alerts/${a.id}/dismiss`).catch(err); load(); };
  return (
    <Table cols={["Date", "Ambassador", "Type", "Detail", "Status", ""]} testid="admin-fraud-table" min={700}>
      {rows.length === 0 && <tr><td colSpan={6} className="p-4 text-center text-zinc-500">No fraud alerts.</td></tr>}
      {rows.map((a) => (
        <tr key={a.id} className="border-t border-[var(--border-warm)]" data-testid={`fraud-row-${a.id}`}>
          <td className="p-2">{a.created_at.slice(0, 10)}</td><td className="p-2">{a.ambassador_code}</td><td className="p-2 text-red-700">{a.kind.replace(/_/g, " ")}</td>
          <td className="p-2 text-xs">{a.detail}</td><td className="p-2 capitalize">{a.status}{a.resolution && ` (${a.resolution})`}</td>
          <td className="p-2">{a.status === "open" && <button className="chip text-xs" onClick={() => dismiss(a)} data-testid={`fraud-dismiss-${a.id}`}>Dismiss</button>}</td>
        </tr>
      ))}
    </Table>
  );
};

export const ReportsPanel = () => {
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/admin/ambassador-reports").then((x) => setR(x.data)).catch(err); }, []);
  if (!r) return null;
  const rows = (list, key) => list.map((x) => <tr key={x[key]} className="border-t border-[var(--border-warm)]"><td className="p-2">{x[key]}{x.name && <span className="text-xs text-zinc-500"> · {x.name}</span>}</td><td className="p-2">{x.enrolments}</td><td className="p-2">{eur(x.revenue)}</td><td className="p-2">{eur(x.commission)}</td></tr>);
  const empty = <tr><td colSpan={4} className="p-3 text-center text-zinc-500">No data yet.</td></tr>;
  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="admin-ambassador-reports">
      <div className="lg:col-span-2"><h3 className="font-display text-xl mb-2">Revenue by ambassador</h3><Table cols={["Ambassador", "Enrolments", "Revenue", "Commission"]} min={500} testid="report-by-ambassador">{r.by_ambassador.length ? rows(r.by_ambassador, "code") : empty}</Table></div>
      <div><h3 className="font-display text-xl mb-2">Enrolments by programme</h3><Table cols={["Programme", "Enrolments", "Revenue", "Commission"]} min={400} testid="report-by-programme">{r.by_programme.length ? rows(r.by_programme, "programme") : empty}</Table></div>
      <div><h3 className="font-display text-xl mb-2">By quarter</h3><Table cols={["Quarter", "Enrolments", "Revenue", "Commission"]} min={400} testid="report-by-quarter">{r.by_quarter.length ? rows(r.by_quarter, "quarter") : empty}</Table></div>
    </div>
  );
};
